import {
  Container,
  Paper,
  Box,
  Typography,
  Breadcrumbs,
  Button,
  Alert,
  TextField,
  InputAdornment
} from '@mui/material';
import { Home, Notifications, Business, Add, Search } from '@mui/icons-material';
import React, { useState, useEffect } from 'react';
import {
  OrganizationNotificationTrigger,
  NotificationSetupTemplate,
  PaginatedNotificationSetupTemplateDTO
} from '../../services/Hub/notificationCenter/types';
import OrganizationSelect from './components/OrganizationSelect';
import TriggersTable from './components/TriggersTable';
import SetupTemplatesModal from './components/SetupTemplatesModal';
import EditTriggerModal from './components/EditTriggerModal';
import DeleteTriggerDialog from './components/DeleteTriggerDialog';

type Organization = { id: string; fantasyName: string };

type Props = {
  selectedOrganization: string | null;
  onOrganizationChange: (id: string) => void;
  triggers: OrganizationNotificationTrigger[];
  triggersPagination: {
    total: number;
    currentPage: number;
    itemsPerPage: number;
  };
  isLoading: boolean;
  error: string | null;
  organizations: Organization[];
  isLoadingOrganizations: boolean;
  setupTemplatesData: PaginatedNotificationSetupTemplateDTO;
  showSetupTemplatesModal: boolean;
  showEditTriggerModal: boolean;
  selectedTrigger: OrganizationNotificationTrigger | null;
  isLoadingTriggerDetails: boolean;
  isCreatingTrigger?: boolean;
  onEditTrigger: (trigger: OrganizationNotificationTrigger) => void;
  onDeleteTrigger: (triggerId: string) => void;
  onUpdateTrigger: (triggerData: Partial<OrganizationNotificationTrigger>) => void;
  onPageChange: (page: number, itemsPerPage: number) => void;
  onSearch: (searchTerm: string) => void;
  onOpenSetupTemplatesModal: () => void;
  onCloseSetupTemplatesModal: () => void;
  onCloseEditTriggerModal: () => void;
  onCreateTriggerFromTemplate: (template: NotificationSetupTemplate) => void;
  onLoadSetupTemplatesPage: (page: number, itemsPerPage: number) => void;
  isLoadingSetupTemplates?: boolean;
};

export default function OrganizationNotificationsView({
  selectedOrganization,
  onOrganizationChange,
  triggers,
  triggersPagination,
  isLoading,
  error,
  organizations,
  isLoadingOrganizations,
  setupTemplatesData,
  showSetupTemplatesModal,
  showEditTriggerModal,
  selectedTrigger,
  isLoadingTriggerDetails,
  isCreatingTrigger = false,
  onEditTrigger,
  onDeleteTrigger,
  onUpdateTrigger,
  onPageChange,
  onSearch,
  onOpenSetupTemplatesModal,
  onCloseSetupTemplatesModal,
  onCloseEditTriggerModal,
  onCreateTriggerFromTemplate,
  onLoadSetupTemplatesPage,
  isLoadingSetupTemplates = false
}: Props) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [triggerToDelete, setTriggerToDelete] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (selectedOrganization) {
      onSearch(debouncedSearchTerm);
    }
  }, [debouncedSearchTerm, selectedOrganization]);

  const handleDeleteClick = (triggerId: string) => {
    setTriggerToDelete(triggerId);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    if (triggerToDelete) {
      onDeleteTrigger(triggerToDelete);
      setShowDeleteConfirm(false);
      setTriggerToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setTriggerToDelete(null);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1.5 }}>
          <Typography color="inherit" sx={{ display: 'flex', alignItems: 'center' }}>
            <Home sx={{ mr: 0.5 }} fontSize="inherit" />
            Início
          </Typography>
          <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
            <Notifications sx={{ mr: 0.5 }} fontSize="inherit" />
            Notificações da Organização
          </Typography>
        </Breadcrumbs>

        <Typography variant="h4" component="h1" gutterBottom fontWeight={600}>
          Notificações da Organização
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          Gerencie triggers de notificação específicos da sua organização.
        </Typography>
      </Box>

      <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Box
          sx={{
            p: 3,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'grey.50',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2
          }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Business sx={{ color: 'primary.main', fontSize: 28 }} />
            <Box>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Seleção de Organização
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Escolha uma organização para gerenciar seus triggers
              </Typography>
            </Box>
          </Box>

          <Box sx={{ minWidth: 250 }}>
            <OrganizationSelect
              organizations={organizations}
              selectedOrganization={selectedOrganization}
              onOrganizationChange={onOrganizationChange}
              isLoading={isLoadingOrganizations}
            />
          </Box>
        </Box>
        {selectedOrganization && (
          <Box
            sx={{
              px: 3,
              py: 2,
              display: 'flex',
              gap: 2,
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
            <TextField
              placeholder="Buscar triggers..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              size="small"
              sx={{
                minWidth: 250,
                maxWidth: 400,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                )
              }}
            />

            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              onClick={onOpenSetupTemplatesModal}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1.5
              }}>
              Novo Trigger
            </Button>
          </Box>
        )}
        {error && (
          <Box sx={{ px: 3, pt: 3 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}

        {selectedOrganization && (
          <Box sx={{ px: 3, pb: 3 }}>
            <TriggersTable
              triggers={triggers}
              pagination={triggersPagination}
              isLoading={isLoading}
              onEditTrigger={onEditTrigger}
              onDeleteTrigger={handleDeleteClick}
              onPageChange={onPageChange}
            />
          </Box>
        )}
      </Paper>

      <SetupTemplatesModal
        open={showSetupTemplatesModal}
        onClose={onCloseSetupTemplatesModal}
        setupTemplatesData={setupTemplatesData}
        onSelectTemplate={onCreateTriggerFromTemplate}
        onLoadPage={onLoadSetupTemplatesPage}
        isLoading={isLoadingSetupTemplates}
        isCreating={isCreatingTrigger}
      />

      <EditTriggerModal
        open={showEditTriggerModal}
        onClose={onCloseEditTriggerModal}
        trigger={selectedTrigger}
        onSave={onUpdateTrigger}
        isLoading={isLoadingTriggerDetails}
      />

      <DeleteTriggerDialog
        open={showDeleteConfirm}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />
    </Container>
  );
}
